import { useState } from "react";
import { X, MapPin, Car, MessageSquare, Clock, UserRound, ChevronLeft, ChevronRight, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api, directionsUrl, errMsg, formatLeft } from "../lib/api";

export const SpotSheet = ({ spot, now, onClose, onChanged, group }) => {
  const [busy, setBusy] = useState(false);
  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/spots/${spot.id}/${path}`);
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const btn = "h-12 flex-1 rounded-full font-semibold transition-colors disabled:opacity-50";
  return (
    <div data-testid="spot-sheet" className="absolute z-[900] left-3 right-3 bottom-24 md:bottom-6 md:left-auto md:right-6 md:w-96 rounded-2xl bg-stone-900/90 backdrop-blur-xl border border-amber-500/20 p-5 shadow-2xl rise">
      {group && group.total > 1 && (
        <div data-testid="spot-group-nav" className="flex items-center justify-between mb-3 -mt-1 rounded-full bg-stone-800/80 px-2 py-1">
          <button data-testid="spot-group-prev" onClick={group.onPrev} className="h-8 w-8 grid place-items-center rounded-full hover:bg-stone-700 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="spot-group-position" className="text-xs font-semibold text-stone-300">Posto {group.index + 1} di {group.total} in questo punto</span>
          <button data-testid="spot-group-next" onClick={group.onNext} className="h-8 w-8 grid place-items-center rounded-full hover:bg-stone-700 transition-colors"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono-x text-[11px] uppercase tracking-wider text-amber-400">
            {spot.status === "reserved" ? "Prenotato" : "Si libera tra"}
          </p>
          <p data-testid="spot-sheet-countdown" className="font-display text-4xl font-extrabold">{formatLeft(spot.leave_at, now)}</p>
        </div>
        <button data-testid="spot-sheet-close" onClick={onClose} className="h-9 w-9 grid place-items-center rounded-full hover:bg-stone-800 transition-colors">
          <X className="h-5 w-5" />
        </button>
      </div>
      <ul className="mt-4 space-y-2 text-sm text-stone-300">
        <li className="flex gap-2"><MapPin className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /><span data-testid="spot-sheet-address">{spot.address}</span></li>
        <li className="flex gap-2"><UserRound className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{spot.is_mine ? "Il tuo posto" : spot.leaver_label}</li>
        {spot.vehicle && <li className="flex gap-2"><Car className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{spot.vehicle}</li>}
        {spot.note && <li className="flex gap-2"><MessageSquare className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{spot.note}</li>}
        <li className="flex gap-2"><Clock className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />Partenza prevista alle {new Date(spot.leave_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</li>
      </ul>
      <div className="mt-5 flex gap-2">
        {!spot.is_mine && spot.status === "active" && (
          <button data-testid="reserve-spot-button" disabled={busy} onClick={() => act("reserve", "Posto prenotato in forma anonima")} className={`${btn} bg-[#E0533C] hover:bg-[#C8432D] text-white`}>
            Prenota posto
          </button>
        )}
        {spot.reserved_by_me && (
          <>
            <button data-testid="complete-spot-button" disabled={busy} onClick={() => act("complete", "Ottimo, buon parcheggio!")} className={`${btn} bg-emerald-500 hover:bg-emerald-600 text-stone-950`}>
              Ho parcheggiato
            </button>
            <button data-testid="unreserve-spot-button" disabled={busy} onClick={() => act("unreserve", "Prenotazione annullata")} className={`${btn} border border-stone-600 hover:bg-stone-800`}>
              Annulla
            </button>
          </>
        )}
        {!spot.is_mine && spot.status === "reserved" && !spot.reserved_by_me && (
          <p className="text-sm text-stone-400">Questo posto è già stato prenotato da un altro utente.</p>
        )}
        {spot.is_mine && (
          <button data-testid="cancel-spot-button" disabled={busy} onClick={() => act("cancel", "Segnalazione annullata")} className={`${btn} border border-stone-600 hover:bg-stone-800`}>
            Annulla segnalazione
          </button>
        )}
      </div>
      {spot.reserved_by_me && (
        <a data-testid="spot-directions-button" href={directionsUrl(spot.lat, spot.lng)} target="_blank" rel="noopener noreferrer"
          className="mt-2 h-12 w-full rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold inline-flex items-center justify-center gap-2 transition-colors">
          <Navigation className="h-4 w-4" /> Percorso con Google Maps
        </a>
      )}
    </div>
  );
};
